import { canUseDOM } from '@/app/common/utils/canUseDOM';

export enum PLATFORM_DATA_EVENT_NAME {
  VISIT = 'visit',
  TEALIUM_COOKIE_GO = 'Tealium_cookieGO',
  COOKIE_GO = 'cookieGO',
}

const hasPlatformDataEventFrom = (eventName: string, fromIndex: number): boolean => {
  if (!canUseDOM || !Array.isArray(window.platformData)) return false;
  return window.platformData.slice(fromIndex).some((data) => (data as { event?: string })?.event === eventName);
};

export const isPlatformDataEventTracked = (eventName: string): boolean => hasPlatformDataEventFrom(eventName, 0);

type WaitForPlatformDataEventParams = {
  eventName: string;
  /** Only entries at this index or later count, so a previous page's event of the same name is ignored. */
  fromIndex?: number;
  intervalMs?: number;
  timeoutMs?: number;
  signal?: AbortSignal;
};

/**
 * Resolves `true` as soon as `window.platformData` holds an entry with the given `event` name at
 * `fromIndex` or later, `false` on timeout or when `signal` aborts.
 */
export const waitForPlatformDataEvent = ({
  eventName,
  fromIndex = 0,
  intervalMs = 250,
  timeoutMs = 60_000,
  signal,
}: WaitForPlatformDataEventParams): Promise<boolean> => {
  if (!canUseDOM || signal?.aborted) return Promise.resolve(false);
  if (hasPlatformDataEventFrom(eventName, fromIndex)) return Promise.resolve(true);

  return new Promise((resolve) => {
    let interval: ReturnType<typeof setInterval> | undefined;
    const startedAt = Date.now();

    const finish = (result: boolean) => {
      clearInterval(interval);
      signal?.removeEventListener('abort', onAbort);
      resolve(result);
    };

    function onAbort() {
      finish(false);
    }

    interval = setInterval(() => {
      if (hasPlatformDataEventFrom(eventName, fromIndex)) {
        finish(true);
      } else if (Date.now() - startedAt >= timeoutMs) {
        finish(false);
      }
    }, intervalMs);

    signal?.addEventListener('abort', onAbort, { once: true });
  });
};
